/**
 * OS からドロップされたファイル・フォルダを、相対パス付きのアップロードバッチへ展開する。
 * DOM 型に直接依存せず構造的型で受けることで、フェイクだけでユニットテストできる。
 */

export interface OsFileEntry {
	isDirectory: false;
	name: string;
	file(onSuccess: (file: File) => void, onError: (err: unknown) => void): void;
}

export interface OsDirectoryEntry {
	isDirectory: true;
	name: string;
	createReader(): OsDirectoryReader;
}

export interface OsDirectoryReader {
	readEntries(onSuccess: (entries: OsEntry[]) => void, onError: (err: unknown) => void): void;
}

export type OsEntry = OsFileEntry | OsDirectoryEntry;

export interface OsDropItem {
	kind: string;
	/** WICG ドラフト API。未実装ブラウザでは関数自体が存在せず、実装済みでも null を返しうる。 */
	webkitGetAsEntry?: () => OsEntry | null;
	getAsFile(): File | null;
}

/** DataTransfer のうち走査に必要な部分だけを表す。 */
export interface OsDropSource {
	items: Iterable<OsDropItem>;
	files: Iterable<File>;
}

export interface DroppedUploadFile {
	file: File;
	/** ドロップしたフォルダ構造を保った相対パス (ルート直下のファイルはファイル名そのもの)。 */
	relativePath: string;
}

export interface DroppedUploadBatch {
	files: DroppedUploadFile[];
	/** 空の末端フォルダの相対パス。親フォルダは作成時に暗黙に作成される。 */
	emptyDirectories: string[];
	/** FileSystemEntry API を利用できず、ファイル一覧へ平坦化したか。 */
	usedFlatFallback: boolean;
}

type CollectedDropContent = Pick<DroppedUploadBatch, "files" | "emptyDirectories">;

/**
 * 実 DataTransfer を走査用の構造的型として扱う。DOM の FileSystemEntry は isDirectory が
 * boolean 型でファイル・ディレクトリの判別共用体へそのまま代入できないため、境界のここで一度だけ変換する。
 */
export function asOsDropSource(dataTransfer: DataTransfer): OsDropSource {
	return dataTransfer as unknown as OsDropSource;
}

/**
 * ドロップされた項目をファイル一覧、空フォルダ一覧、フォールバック状態へ展開する。items /
 * files は drop イベントの同期フェーズを抜けると読めなくなるため、この関数は drop ハンドラから
 * await を挟まずに呼ぶこと (退避はすべて同期的に済ませ、走査だけを非同期で行う)。
 */
export async function extractDroppedFiles(source: OsDropSource): Promise<DroppedUploadBatch> {
	const items = [...source.items].filter((item) => item.kind === "file");
	const entries = items.map((item) =>
		typeof item.webkitGetAsEntry === "function" ? item.webkitGetAsEntry() : null,
	);

	if (items.length === 0) {
		return { files: [], emptyDirectories: [], usedFlatFallback: false };
	}

	if (entries.every((entry): entry is OsEntry => entry !== null)) {
		const { files, emptyDirectories } = await collectDroppedFiles(entries, "");
		return {
			files,
			emptyDirectories,
			usedFlatFallback: false,
		};
	}

	const itemFiles = items.map((item) => item.getAsFile());
	const flatFiles = itemFiles.filter((file): file is File => file !== null);
	const files = flatFiles.length === items.length ? flatFiles : [...source.files];
	return {
		files: files.map((file) => ({ file, relativePath: file.name })),
		emptyDirectories: [],
		usedFlatFallback: true,
	};
}

/**
 * エントリを深さ優先で走査し、<paramref name="prefix"/> を連結した相対パス付きの
 * ファイル一覧と空の末端フォルダの相対パスを返す。entry.file() が返す File は
 * webkitRelativePath が空のため、走査中に組み立てたパスを使う。
 */
export async function collectDroppedFiles(
	entries: readonly OsEntry[],
	prefix: string,
): Promise<CollectedDropContent> {
	const files: DroppedUploadFile[] = [];
	const emptyDirectories: string[] = [];
	for (const entry of entries) {
		const relativePath = prefix ? `${prefix}/${entry.name}` : entry.name;
		if (entry.isDirectory) {
			const children = await readAllEntries(entry.createReader());
			if (children.length === 0) {
				emptyDirectories.push(relativePath);
				continue;
			}
			const nested = await collectDroppedFiles(children, relativePath);
			files.push(...nested.files);
			emptyDirectories.push(...nested.emptyDirectories);
		} else {
			files.push({ file: await readEntryFile(entry), relativePath });
		}
	}
	return { files, emptyDirectories };
}

/** readEntries は 1 回で一部しか返さない (Chrome は最大 100 件) ため、空になるまで繰り返す。 */
async function readAllEntries(reader: OsDirectoryReader): Promise<OsEntry[]> {
	const all: OsEntry[] = [];
	for (;;) {
		const batch = await new Promise<OsEntry[]>((resolve, reject) => {
			reader.readEntries(resolve, reject);
		});
		if (batch.length === 0) return all;
		all.push(...batch);
	}
}

function readEntryFile(entry: OsFileEntry): Promise<File> {
	return new Promise((resolve, reject) => {
		entry.file(resolve, reject);
	});
}
